import Bugsnag from '@bugsnag/js'
import isEmpty from 'lodash.isempty'
import uniqBy from 'lodash.uniqby'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { fetchGridPreviewByAccount } from 'api'
import type { AccountSelectorProps } from 'Components'
import { PostPreviewGrid } from 'Components'
import Dropdown from 'Components/Common/Dropdown'
import PoweredBySked from 'Components/Common/PoweredBySked'
import { PostPreviewGridWrap } from 'Components/PostPreviewSection'
import { normalizeGridPreviewAllPosts } from 'normalizer'
import { ensureError } from 'utils/ensureError'
import getInstagramAccountOption from 'utils/getInstagramAccountOption'
import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'

import { Title, Desc } from './Components/CommonStyleComponents'

const Wrap = styled.div`
  margin: 24px auto;
  width: 600px;
  position: relative;
  min-height: 100vh;
`

const DescStyled = styled(Desc)<{ isHasNoMargin: boolean }>`
  margin-bottom: ${({ isHasNoMargin }) => (isHasNoMargin ? 0 : 24)}px;
`

const DropdownWrap = styled.div`
  margin: 0 auto;
  width: 300px;
`

const PostPreviewGridWrapStyled = styled(PostPreviewGridWrap)`
  padding-bottom: 100px;

  > div {
    position: relative;
    width: 600px;
    margin: 0 auto;

    > svg {
      width: 525px;
    }

    > .container {
      width: 600px;
      padding-top: 62px;
      margin-top: 12px;

      > div {
        font-size: 14px;

        &:first-child {
          margin: 20px auto;
        }

        &.grid-container {
          width: 380px;
          max-height: 600px; /* Increased to accommodate taller grid items */

          > div {
            width: 100%;
            aspect-ratio: 4 / 5; /* Using aspect-ratio property for 4:5 ratio */

            &:nth-child(7) {
              img {
                border-bottom-left-radius: 0;
              }
            }
          }
        }

        > img {
          width: 48px;
          height: 48px;
        }
      }
    }
  }
`

const GridPreviewWrap = styled.div`
  text-align: center;
  font-size: 16px;

  > p {
    margin-top: 24px;
  }
`

const PoweredBySkedStyled = styled(PoweredBySked)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
`

interface InstagramGridPageProps {
  accountSelectorProps: AccountSelectorProps
  isReadOnly: boolean
}

const InstagramGridPage = ({
  accountSelectorProps,
  isReadOnly,
}: InstagramGridPageProps) => {
  const reviewKey = getReviewKeyFromURL(window.location.href)

  const [gridData, setGridData] = useState<any>([])
  const [gridDataCached, setGridDataCached] = useState([])

  const [isGridPreviewLoading, setIsGridPreviewLoading] = useState(false)

  const [selectedAccountInfo, setSelectedAccountInfo] = useState({
    name: '',
    handler_name: '',
    avatar: '',
  })

  const accountDropdownOption = getInstagramAccountOption(
    accountSelectorProps.accounts
  )
  const accountIds = accountDropdownOption.map((acc) => acc.id)

  useEffect(() => {
    if (isEmpty(gridDataCached) && accountIds.length > 0) {
      const fetchData = async () => {
        setIsGridPreviewLoading(true)
        try {
          if (!accountIds || accountIds.length === 0) return

          const posts = await Promise.all(
            accountIds.map((id) => fetchGridPreviewByAccount(reviewKey, id))
          )

          const gridPosts = uniqBy(
            posts.filter((p) => Array.isArray(p)).flat(1),
            'id'
          )

          if (gridPosts.length > 0) {
            setGridData(
              normalizeGridPreviewAllPosts(gridPosts).filter((post: any) =>
                post.accounts.some((id: string) => {
                  return accountIds[0] === id
                })
              )
            )

            setGridDataCached(normalizeGridPreviewAllPosts(gridPosts))
          }
        } catch (error) {
          Bugsnag.notify(ensureError(error))
        } finally {
          setIsGridPreviewLoading(false)
        }
      }
      fetchData()
    }
  }, [gridDataCached, accountIds])

  useEffect(() => {
    if (gridData.length && gridData[0].accountInfo) {
      setSelectedAccountInfo(gridData[0].accountInfo)
    }
  }, [gridData])

  return (
    <Wrap>
      <Title>Instagram Grid</Title>
      <DescStyled isHasNoMargin={accountIds.length <= 1}>
        Upcoming posts for Instagram will appear in the grid preview.
      </DescStyled>
      {accountIds.length > 1 && (
        <DropdownWrap>
          <Dropdown
            name="accounts"
            placeholder="Select from options"
            onChange={(selected) => {
              const selectedId = selected.value

              const nextGridData = gridDataCached.filter((post: any) =>
                post.accounts.some((id: string) => selectedId === id)
              )

              if (nextGridData.length > 0) {
                setGridData(nextGridData)
              } else {
                setGridData([])
              }
            }}
            options={accountDropdownOption}
          />
        </DropdownWrap>
      )}

      <GridPreviewWrap>
        {isGridPreviewLoading ? (
          <>
            <br />
            loading...
          </>
        ) : isEmpty(gridData) ? (
          <>
            <br />
            We couldn&apos;t find any upcoming posts
          </>
        ) : (
          <PostPreviewGridWrapStyled>
            <PostPreviewGrid
              posts={gridData}
              account={selectedAccountInfo}
              isReadOnly={isReadOnly}
            />
          </PostPreviewGridWrapStyled>
        )}
      </GridPreviewWrap>

      <PoweredBySkedStyled />
    </Wrap>
  )
}

export default InstagramGridPage
